import { Box } from "theme-ui";
import PropTypes from "prop-types";
import React from "react";
import { noop } from "sites-common/utils/lodash";
import { DynamicFieldStyle1 as InputWithIconAndLabel } from "sites-common/components/DynamicField";
import { FlexColumn, DayPickerInput } from "../..";
import { formatToIST } from "./utils";

const donorBankOptions = [
  { name: "", label: "SELECT BANK" },
  { name: "YES BANK", label: "YES BANK" },
  { name: "IDBI BANK", label: "IDBI BANK" },
  { name: "ICICI BANK LIMITED", label: "ICICI BANK LIMITED" },
  { name: "AXIS BANK", label: "AXIS BANK" },
  {
    name: "KOTAK MAHINDRA BANK LIMITED",
    label: "KOTAK MAHINDRA BANK LIMITED",
  },
  { name: "HDFC BANK", label: "HDFC BANK" },
  { name: "OTHERS", label: "OTHERS" },
];

export const NetBankingSectionLayout = ({
  donorBankInput,
  dayPickerInput,
  ifscCodeInput,
}) => (
  <FlexColumn
    gap="5px"
    sx={{
      "& ::placeholder, input, select": {
        fontSize: "1rem!important",
      },
    }}
  >
    <Box
      sx={{
        fontSize: "12px",
        backgroundColor: "background2",
        padding: "7px",
        margin: "0px 0px 5px !important",
      }}
    >
      Please fill the below details, the SMSF Bank details for setting up the
      standing instructions will be emailed to you
    </Box>

    {/* DONOR BANK */}
    <InputWithIconAndLabel
      {...donorBankInput}
      variant="border-bottom"
      type="select"
      label="Donor's Bank"
    />

    {/* IFSC CODE INPUT */}
    <InputWithIconAndLabel
      type="text"
      label="Donor's IFSC Code"
      variant="border-bottom"
      {...ifscCodeInput}
    />

    {/* STANDING INSTRUCTIONS DATE */}
    <DayPickerInput
      {...dayPickerInput}
      label="Date of standing instruction"
      inputProps={{
        readOnly: true,
        variant: "border-bottom",
      }}
    />
  </FlexColumn>
);

NetBankingSectionLayout.propTypes = {
  donorBankInput: PropTypes.shape({}),
  dayPickerInput: PropTypes.shape({}),
  ifscCodeInput: PropTypes.shape({}),
};

NetBankingSectionLayout.defaultProps = {
  donorBankInput: {},
  dayPickerInput: {},
  ifscCodeInput: {},
};

const useNetBakingSection = (props) => {
  const {
    standing_date = null,
    donor_bank = "",
    donor_ifsc_code = "",
    onChange = noop,
  } = props;

  const onChangeNetBanking = React.useCallback(
    (update) => {
      onChange({
        standing_date,
        donor_bank,
        donor_ifsc_code,
        ...update,
      });
    },
    [standing_date, donor_bank, donor_ifsc_code, onChange]
  );

  const dayPickerInput = React.useMemo(
    () => ({
      value: standing_date,
      formatDate: formatToIST,
      inputProps: {
        readOnly: true,
      },
      dayPickerProps: {
        onDayClick: (standingDate, { disabled }) =>
          !disabled && onChangeNetBanking({ standing_date: standingDate }),
        modifiers: {
          disabled: {
            before: new Date(),
          },
        },
      },
    }),
    [standing_date, onChangeNetBanking]
  );
  const ifscCodeInput = React.useMemo(
    () => ({
      value: donor_ifsc_code,
      onChange: (donorIfscCode) => {
        onChangeNetBanking({ donor_ifsc_code: donorIfscCode });
      },
    }),
    [donor_ifsc_code, onChangeNetBanking]
  );
  const donorBankInput = React.useMemo(
    () => ({
      options: donorBankOptions,
      value: donor_bank,
      onChange: (donorBank) => onChangeNetBanking({ donor_bank: donorBank }),
    }),
    [donor_bank, onChangeNetBanking]
  );
  return {
    dayPickerInput,
    ifscCodeInput,
    donorBankInput,
  };
};

const NetBankingSection = (props) => {
  const {
    onChange = noop,
    standing_date = null,
    donor_bank = "",
    donor_ifsc_code = "",
  } = props;
  const netBankingSection = useNetBakingSection({
    standing_date,
    donor_bank,
    donor_ifsc_code,
    onChange,
  });
  return <NetBankingSectionLayout {...netBankingSection} />;
};

NetBankingSection.propTypes = {
  donor_ifsc_code: PropTypes.string,
  standing_date: PropTypes.instanceOf(Date),
  donor_bank: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

NetBankingSection.defaultProps = {
  donor_ifsc_code: "",
  standing_date: new Date(),
  donor_bank: "",
};

export default React.memo(NetBankingSection);
